import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CommonMovieCard from "./CommonMovieCard";
import { getCommonInformedness, submitCommonInformedness } from "../service/api";
import { commonErrorMessage, newClientResponseId } from "./commonSubmission";

export const INFORMEDNESS_OPTIONS = [
  ["seen_judgeable", "I had seen it and remembered enough to have an opinion."],
  ["unseen_or_not_remembered_judgeable", "I had not seen it, or did not remember it well, but I had enough information to have an opinion."],
  ["unseen_or_not_remembered_not_judgeable", "I had not seen it, or did not remember it well, and I did not have enough information to have an opinion."],
];

export default function CommonInformednessTask({ userId, commonSessionId, onPhaseChange }) {
  const [items, setItems] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const headingRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const response = await getCommonInformedness({ userId, commonSessionId });
      if (response.phase !== "common_informedness_in_progress") {
        onPhaseChange(response.phase, commonSessionId);
        return;
      }
      setItems(response.items);
      setAnswers(Object.fromEntries(response.items.filter((item) => item.informedness).map((item) => [item.movieId, item.informedness])));
      setError("");
    } catch (requestError) { setError(commonErrorMessage(requestError)); }
  }, [commonSessionId, onPhaseChange, userId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (items.length) headingRef.current?.focus(); }, [items.length]);
  const savedIds = useMemo(() => new Set(items.filter((item) => item.informedness).map((item) => item.movieId)), [items]);

  const submit = async (event) => {
    event.preventDefault();
    if (items.some((item) => !answers[item.movieId])) { setError("Please choose one statement for every movie."); return; }
    setSubmitting(true); setError("");
    try {
      let phase = "common_informedness_in_progress";
      for (const item of items) {
        if (savedIds.has(item.movieId)) continue;
        const receipt = await submitCommonInformedness({
          userId, commonSessionId, movieId: item.movieId, informedness: answers[item.movieId], clientResponseId: newClientResponseId(),
        });
        phase = receipt.phase;
      }
      onPhaseChange(phase, commonSessionId);
    } catch (requestError) {
      setError(`${commonErrorMessage(requestError)} Saved answers will be restored when you retry.`);
      await load();
    } finally { setSubmitting(false); }
  };

  if (!items.length && !error) return <p role="status">Loading movie informedness…</p>;
  return (
    <section className="common-informedness">
      <h2 ref={headingRef} tabIndex="-1">Information available for each movie</h2>
      <p>Thinking about the movie comparisons you just completed, which statement best describes each movie?</p>
      <form onSubmit={submit}>
        <div className="common-informedness-grid">
          {items.map((item) => (
            <fieldset className="common-informedness-card" key={item.movieId}>
              <legend className="sr-only">{item.movie.title}</legend>
              <CommonMovieCard movie={item.movie} compact />
              <div className="common-informedness-options">
                {INFORMEDNESS_OPTIONS.map(([value, label]) => (
                  <label key={value}>
                    <input type="radio" name={`informedness-${item.movieId}`} value={value}
                      checked={answers[item.movieId] === value}
                      disabled={submitting || savedIds.has(item.movieId)}
                      onChange={() => setAnswers((current) => ({ ...current, [item.movieId]: value }))} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ))}
        </div>
        {error && <div className="common-error" role="alert">{error}</div>}
        <button type="submit" className="submit-button" disabled={submitting || !items.length}>Save and continue to ranking</button>
      </form>
    </section>
  );
}
